import { useMemo, useState } from "react";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { AppHeader } from "@/components/app/AppHeader";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAllProfiles, useAuditLog } from "@/hooks/use-admin-data";
import { formatIst } from "@/lib/tz";

export const Route = createFileRoute("/_app/_admin/admin/audit-log")({
  component: () => <Navigate to="/admin/settings" search={{ tab: "audit-log" } as never} />,
});

export function AuditLogPage() {
  const { data: rows = [], isLoading } = useAuditLog();
  const { data: profiles = [] } = useAllProfiles();
  const profileById = useMemo(
    () => new Map(profiles.map((p) => [p.id, p])),
    [profiles],
  );
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");

  const actions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.action_type))).sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (actionFilter !== "all" && r.action_type !== actionFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const admin = profileById.get(r.admin_id ?? "")?.full_name ?? "";
        const target = profileById.get(r.target_user_id ?? "")?.full_name ?? "";
        return (
          r.action_type.toLowerCase().includes(q) ||
          admin.toLowerCase().includes(q) ||
          target.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [rows, search, actionFilter, profileById]);

  return (
    <>
      <AppHeader subtitle="Every admin action is recorded here" />
      <div className="px-[22px] pb-6">
        <div className="rounded-lg border border-border bg-card shadow-sm">
          <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
                History
              </div>
              <h2 className="text-[16px] font-bold">{rows.length} events</h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="h-9 w-56 text-[12px]"
              />
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="h-9 w-44 text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  {actions.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Admin</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5} className="h-20 text-center text-[12px] text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="h-20 text-center text-[12px] text-muted-foreground">
                    No events.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="whitespace-nowrap text-[12px] text-muted-foreground">
                    {formatIst(r.created_at, "EEE, dd MMM HH:mm")}
                  </TableCell>
                  <TableCell className="text-[12px] font-medium">{r.action_type}</TableCell>
                  <TableCell className="text-[12px]">
                    {profileById.get(r.admin_id ?? "")?.full_name ?? "—"}
                  </TableCell>
                  <TableCell className="text-[12px]">
                    {profileById.get(r.target_user_id ?? "")?.full_name ?? "—"}
                  </TableCell>
                  <TableCell>
                    {r.after_data ? (
                      <code className="line-clamp-2 rounded bg-muted px-2 py-1 text-[10px]">
                        {JSON.stringify(r.after_data)}
                      </code>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}
